import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import React from "react";
import clsx from "clsx";
import { HttpStatusCode } from "axios";

import {
  ON_OFF_STATUS,
  VOTE_ITEM_TYPE,
  VOTE_STATUS,
} from "../../../consts/hotColdVote";
import { MANAGER_ROLE } from "../../../consts/member";

import { useUserContext } from "../../../contexts/userContext";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";
import { RefreshIcon } from "../../../hooks/useStoreHeader";

import {
  HotColdAirInfo,
  HotColdSensor,
  HotColdSpaceAirInfo,
  VotePeriodInfo,
} from "../../../services/hotColdVoteServices";
import {
  getManagerBuildingSpaceAir,
  patchManagerBuildingSpaceSensorStatus,
} from "../../../services/managerBuildingServices";
import { TAG_TYPE } from "../../../services/iotServices";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonCheckbox from "../../../components/Input/Checkbox";
import CommonButton from "../../../components/Button";
import CommonPopup from "../../../components/Popup";
import { SelectOption } from "../../../components/Select";
import CommonTextSwitch from "../../../components/Input/TextSwitch";
import VerticalRule from "../../../components/Rule/Vertical";
import { Skeleton } from "../../Talk";

import { NoDataIcon } from "../../Center";

type Props = {};

export default function ManagerHotColdVote(props: Props) {
  const navigate = useNavigate();
  const [user] = useUserContext();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const [checkedList, setCheckedList] = useState<HotColdSpaceAirInfo[]>([]);

  const initialSensor = {
    equipmentStatus: ON_OFF_STATUS.OFF,
    ventilationStatus: ON_OFF_STATUS.OFF,
  } as HotColdSensor;

  const [sensor, setSensor] = useState<Partial<HotColdSensor>>(initialSensor);
  const [popup, setPopup] = useState<"iotApply" | "">("");
  const [hotCold, setHotCold] = useState<HotColdAirInfo>();

  const optionList: SelectOption[] = [
    { value: "floorSort.asc", label: "층 높은 순" },
    { value: "floorSort.desc", label: "층 낮은 순" },
  ];

  // 층에 등록된 정렬순서에 따라 건물마다 필터 결과 조건이 다를 수 있어 필터검색 기능 주석처리. 추후 정책 결정될 시 코드 삭제 또는 주석해제 필요.
  const [selectedSort, setSelectedSort] = useState<SelectOption>(optionList[0]);

  const isAllChecked = hotCold?.floors?.every((floor) =>
    floor.spaces.every((space) =>
      checkedList.find((item) => item.buildingSpaceId === space.buildingSpaceId)
    )
  );

  const findItem = (id: string) => {
    return checkedList.find((item) => item.buildingSpaceId === id);
  };

  const getFloor = (spaceId: string) => {
    return hotCold?.floors?.find((floor) =>
      floor.spaces.some((space) => space.buildingSpaceId === spaceId)
    );
  };

  const columnInfo: HotColdTableType[] = [
    { header: "층" },
    {
      header: (
        <div className="px-1.5 min-w-[36px]">
          <CommonCheckbox
            checked={isAllChecked}
            onClick={() => {
              if (isAllChecked) {
                setCheckedList([]);
              } else {
                const allIds = hotCold?.floors.flatMap((floor) => floor.spaces);
                setCheckedList(allIds ?? []);
              }
            }}
          />
        </div>
      ),
      render: (item) => (
        <div className="w-[24px]">
          <CommonCheckbox
            checked={Boolean(findItem(item.buildingSpaceId))}
            onClick={(value: boolean) => {
              setCheckedList((prev) =>
                value
                  ? [...prev, item]
                  : prev.filter(
                      (prevItem) =>
                        prevItem.buildingSpaceId !== item.buildingSpaceId
                    )
              );
            }}
          />
        </div>
      ),
    },
    {
      header: (
        <div className="flex flex-col justify-center items-start px-1.5 min-w-[70px]">
          공간
          <span className="text-[11px]">{`(온/습도)`}</span>
        </div>
      ),
      render: (item: HotColdSpaceAirInfo) => {
        const temperature = item.tag?.find(
          (tag) => tag.tagTypeId === TAG_TYPE.TEMPERATURE
        );
        const humidity = item.tag?.find(
          (tag) => tag.tagTypeId === TAG_TYPE.HUMIDITY
        );
        const isSensorInstalled = item.tag?.length > 0;
        const isSensorError =
          isSensorInstalled && (!temperature?.value || !humidity?.value);

        const summaryText = isSensorInstalled
          ? isSensorError
            ? "센서 이상 확인"
            : `${temperature?.value?.toFixed(0) ?? ""}${
                temperature?.tagTypeUnit ?? ""
              }/${humidity?.value?.toFixed(0) ?? ""}${
                humidity?.tagTypeUnit ?? ""
              }`
          : "센서 미설치";

        return (
          <div className="flex flex-col font-medium text-sm">
            {item.spaceName}
            <div className="text-xs font-normal text-brand-neutral-60 whitespace-nowrap">
              {`(`}
              <span
                className={clsx({
                  "text-brand-primary-red": isSensorError,
                })}
              >
                {summaryText}
              </span>
              {`)`}
            </div>
          </div>
        );
      },
    },
    {
      header: (
        <div className="flex justify-center items-center min-w-[50px]">{`설비\n/환기`}</div>
      ),
      render: (item: HotColdSpaceAirInfo) => {
        const getText = (status: string | null) => {
          if (!status || status === ON_OFF_STATUS.OFF) return "Off";
          return "On";
        };

        const className = clsx(
          { "text-[#FF4343]": item.equipmentStatus === VOTE_ITEM_TYPE.HOT },
          { "text-[#0496FF]": item.equipmentStatus === VOTE_ITEM_TYPE.COLD }
        );

        return (
          <div className="flex flex-col font-medium text-sm justify-center items-center">
            <div className={className}>{getText(item.equipmentStatus)}</div>
            <div>
              <span className="px-0.5 text-brand-neutral-50">/</span>
              {getText(item.ventilationStatus)}
            </div>
          </div>
        );
      },
    },
    {
      header: (
        <div className="flex flex-col justify-center items-center min-w-[58px]">
          투표결과
          <span className="text-[10px]">(오전/오후)</span>
        </div>
      ),
      render: (item: HotColdSpaceAirInfo) => {
        const getStatusText = (voteInfo: VotePeriodInfo | null) => {
          if (voteInfo?.status === VOTE_STATUS.ONGOING && !voteInfo?.result) {
            return "투표중";
          } else if (voteInfo?.result === VOTE_ITEM_TYPE.GOOD) {
            return "동률";
          }

          return !voteInfo ? "미진행" : `${voteInfo.topRate}%`;
        };

        const getItem = (period: VotePeriodInfo | null) => {
          return (
            <span
              className={clsx("font-medium text-sm", {
                "text-[#FF4343]": period?.result === VOTE_ITEM_TYPE.HOT,
                "text-[#0496FF]": period?.result === VOTE_ITEM_TYPE.COLD,
                "text-[#199C87]": period?.result === VOTE_ITEM_TYPE.GOOD,
              })}
            >
              {getStatusText(period)}
            </span>
          );
        };

        return (
          <div className="flex flex-col justify-center items-center whitespace-pre-line">
            {getItem(item.am)}
            <div>/ {getItem(item.pm)}</div>
          </div>
        );
      },
    },
    {
      header: (
        <div className="flex justify-center items-center min-w-[50px]">{`상세\n보기`}</div>
      ),
      render: (item: HotColdSpaceAirInfo) => {
        const voteId = item.pm?.voteId ?? item.am?.voteId ?? null;
        const floor = getFloor(item.buildingSpaceId);

        return (
          <div className="flex justify-center items-center min-w-[50px]">
            <CommonButton
              variant="tertiary-s"
              onClick={() => {
                const params = new URLSearchParams();
                voteId && params.append("voteId", voteId.toString());
                hotCold?.buildingId &&
                  params.append("buildingId", hotCold?.buildingId);
                hotCold?.buildingName &&
                  params.append("buildingName", hotCold?.buildingName);
                floor?.buildingFloorId &&
                  params.append("buildingFloorId", floor?.buildingFloorId);
                floor?.floorName &&
                  params.append("floorName", floor?.floorName);
                params.append("buildingSpaceId", item.buildingSpaceId);
                params.append("spaceName", item.spaceName);

                navigate(
                  `/manager/manager-hot-cold-vote/detail?${params?.toString()}`
                );
              }}
            >
              이동
            </CommonButton>
          </div>
        );
      },
    },
  ];

  const loadHotColdInfo = async () => {
    if (!user.buildingId) return;

    setIsLoading(true);
    try {
      const res = await getManagerBuildingSpaceAir({
        buildingId: user.buildingId,
        // sort: selectedSort.value,
      });
      if (res.status === HttpStatusCode.Ok) {
        setHotCold(res.data);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open?.(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open?.("더워요 추워요 층 정보를 불러오는데 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    setCheckedList([]);
    loadHotColdInfo();
  };

  useEffect(() => {
    if (checkedList.length === 1) {
      const selectedItem = checkedList.find((item) =>
        Boolean(item.buildingSpaceId)
      );
      setSensor({
        equipmentStatus: selectedItem?.equipmentStatus ?? ON_OFF_STATUS.OFF,
        ventilationStatus: selectedItem?.ventilationStatus ?? ON_OFF_STATUS.OFF,
      });
    } else {
      setSensor(initialSensor);
    }
  }, [checkedList]);

  useEffect(() => {
    if (user.isLoading) return;

    if (user.role?.includes(MANAGER_ROLE.USER)) {
      navigate("/view");
    } else {
      loadHotColdInfo();
    }
  }, [user.isLoading, selectedSort]);

  return (
    <div className="px-5">
      {loadingComponent}
      <StickyHeader
        title="더워요 추워요"
        onBackClick={() => navigate("/view")}
        rightContent={
          <div onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        }
      />

      <div className="flex pb-2.5 text-2xl justify-center items-center font-semibold text-center">
        {user.isLoading ? (
          <Skeleton className="w-[200px] h-[32px] rounded-3xl" />
        ) : (
          user.buildingName
        )}
      </div>

      <div
        className="flex flex-col p-5 gap-2.5 rounded-[10px]"
        style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.1)" }}
      >
        <div className="flex justify-between items-center font-medium text-sm flex-wrap">
          <div className="flex flex-1 min-w-[65px] gap-1 justify-center items-center px-[19px] py-[5px]">
            <div className="flex flex-col gap-1 justify-center items-center">
              <span>난방</span>
              <CommonTextSwitch
                variant="black"
                value={sensor?.equipmentStatus === VOTE_ITEM_TYPE.HOT}
                onClick={(value: boolean) => {
                  setSensor((prev) => ({
                    ...prev,
                    equipmentStatus: value
                      ? VOTE_ITEM_TYPE.HOT
                      : ON_OFF_STATUS.OFF,
                  }));
                }}
              />
            </div>
          </div>
          <VerticalRule className="flex-initial !h-[64px] bg-brand-gray-10 opacity-10" />
          <div className="flex flex-1 min-w-[65px] gap-1 justify-center items-center px-[19px] py-[5px]">
            <div className="flex flex-col gap-1 justify-center items-center">
              <span>냉방</span>
              <CommonTextSwitch
                variant="black"
                value={sensor?.equipmentStatus === VOTE_ITEM_TYPE.COLD}
                onClick={(value: boolean) => {
                  setSensor((prev) => ({
                    ...prev,
                    equipmentStatus: value
                      ? VOTE_ITEM_TYPE.COLD
                      : ON_OFF_STATUS.OFF,
                  }));
                }}
              />
            </div>
          </div>
          <VerticalRule className="flex-initial !h-[64px] bg-brand-gray-10 opacity-10" />
          <div className="flex flex-1 min-w-[65px] gap-1 justify-center items-center px-[19px] py-[5px]">
            <div className="flex flex-col gap-1 justify-center items-center">
              <span>환기</span>
              <CommonTextSwitch
                variant="black"
                value={sensor?.ventilationStatus === ON_OFF_STATUS.ON}
                onClick={(value: boolean) => {
                  setSensor((prev) => ({
                    ...prev,
                    ventilationStatus: value
                      ? ON_OFF_STATUS.ON
                      : ON_OFF_STATUS.OFF,
                  }));
                }}
              />
            </div>
          </div>
        </div>

        <CommonButton
          variant="secondary"
          className="h-[30px] w-full text-sm font-medium"
          onClick={() => {
            if (checkedList.length === 0) {
              open("선택된 공간이 없습니다.");
            } else {
              setPopup("iotApply");
            }
          }}
        >
          적용
        </CommonButton>
      </div>

      {hotCold ? (
        <div className="overflow-x-auto pt-[30px]">
          {/* <div className="flex min-w-[280px] justify-end pb-2.5">
            <CommonSelect
              value={selectedSort?.value}
              optionList={optionList}
              onChange={setSelectedSort}
              className="border-none max-w-[200px] text-wrap text-right"
              wrapperClassName="flex justify-end"
              optionWidth="200px"
              optionHeight="200px"
              optionOrder="right"
              variant="transparent"
            />
          </div> */}
          <HotColdTable data={hotCold} columnInfo={columnInfo} />
        </div>
      ) : (
        <div className="sticky top-[72px] w-full h-40 mt-20 flex flex-col items-center justify-center gap-2">
          <NoDataIcon />
          데이터가 없어요
        </div>
      )}

      <CommonPopup
        isOpen={popup === "iotApply"}
        padding={0}
        onClose={() => setPopup("")}
      >
        <div className="py-[30px] text-center font-medium">
          공간의 상태를 변경하시겠습니까?
        </div>

        <div className="flex">
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            variant="secondary"
            onClick={() => setPopup("")}
          >
            취소
          </CommonButton>
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            onClick={async () => {
              if (!sensor.equipmentStatus || !sensor.ventilationStatus) return;

              setIsLoading(true);
              patchManagerBuildingSpaceSensorStatus({
                buildingSpaceIds: checkedList.map(
                  (item) => item.buildingSpaceId
                ),
                equipmentStatus: sensor.equipmentStatus,
                ventilationStatus: sensor.ventilationStatus,
              })
                .then((res) => {
                  if (res.status === HttpStatusCode.Ok) {
                    open("적용 되었습니다.");
                    handleRefresh();
                  }
                })
                .catch((e: any) => {
                  if (e?.response?.data?.error) {
                    open(e?.response?.data?.error.replace(". ", ".\n"));
                  } else {
                    open(
                      "적용 중 오류가 발생했습니다.\n관리자에게 문의해주세요."
                    );
                  }
                })
                .finally(() => {
                  setIsLoading(false);
                  setPopup("");
                });
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      {simplePopupComponent}
    </div>
  );
}

export type HotColdTableType = {
  header: React.ReactNode;
  render?: (item: HotColdSpaceAirInfo) => React.ReactNode;
  width?: string;
};

export type HotColdTableProps = {
  data: HotColdAirInfo;
  columnInfo: HotColdTableType[];
};

export const HotColdTable = ({ data, columnInfo }: HotColdTableProps) => {
  // 너비고정: [층, 체크박스], 그 외 균등하게 가변 너비로 적용
  const minWidth = `w-[calc((100% - 40px) / ${columnInfo.length - 2})]`;
  const filteredFloors = data.floors.filter(
    (floor) => (floor.spaces?.length ?? 0) > 0
  );
  const lastFloorFirstSpaceId =
    filteredFloors[filteredFloors.length - 1]?.spaces?.[0].buildingSpaceId ??
    "";
  const lastSpaceId = filteredFloors
    .map((floor) => floor.spaces?.[floor.spaces.length - 1].buildingSpaceId)
    .pop();

  return (
    <table className="w-full min-w-[280px]" data-testid="hot-cold-vote-table">
      <thead className="text-[13px] leading-[15px] whitespace-pre-line bg-[#fafafa]">
        <tr className="bg-white border-t-2 border-b border-spacing-1 border-black h-[56px] text-xs leading-[18px] font-normal text-[#6E6E70]">
          {columnInfo.map((column, index) => (
            <th
              key={index}
              className="!font-normal min-w-fit border-b border-b-black"
              style={{
                minWidth: index < 2 ? column.width : minWidth,
              }}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>

      <tbody className="text-xs whitespace-pre-line ">
        {data.floors.map((floor, floorIndex) => {
          // 각 층별로 첫 번째 셀에만 행(row) 수를 계산하여 병합 적용
          const floorRowSpan = floor.spaces.length;
          return floor.spaces.map((space, spaceIndex) => (
            <tr key={space.buildingSpaceId}>
              {columnInfo.map((column, columnIndex) => {
                const tdClassName = clsx("px-1.5 py-2 ");

                if (columnIndex === 0) {
                  if (spaceIndex === 0) {
                    return (
                      // 층 출력
                      <td
                        key={columnIndex}
                        rowSpan={floorRowSpan}
                        className={clsx(
                          "bg-[#fafafa] text-center font-semibold text-base border-r-[#F3F3F3] border-r",
                          tdClassName,
                          {
                            "border-b border-black":
                              space.buildingSpaceId === lastFloorFirstSpaceId,
                            "border-b border-b-[#CCCCCC]":
                              space.buildingSpaceId !== lastFloorFirstSpaceId,
                          }
                        )}
                      >
                        {floor.floorName}
                      </td>
                    );
                  }
                  return null;
                }

                // 나머지 셀 출력
                return (
                  <td
                    key={columnIndex}
                    className={clsx("max-w-[100px]", tdClassName, {
                      "border-b border-b-black":
                        space.buildingSpaceId === lastSpaceId,
                      "border-b border-b-[#CCCCCC]":
                        space.buildingSpaceId !== lastSpaceId,
                    })}
                  >
                    {column.render?.(space)}
                  </td>
                );
              })}
            </tr>
          ));
        })}
      </tbody>
    </table>
  );
};
